<template>
  <q-dialog
    v-model="dialogOpen"
    persistent
    maximized
    transition-show="slide-up"
    transition-hide="slide-down"
  >
    <q-card class="category-documentation-dialog">
      <!-- Header -->
      <q-card-section class="bg-primary text-white">
        <div class="row items-center">
          <div class="col">
            <div class="text-h6 row items-center q-gutter-sm">
              <span>{{ equipment?.name || 'Equipment Documentation' }}</span>
              <!-- Category Badge (when category is detected) -->
              <q-badge
                v-if="category"
                :color="categoryColor"
                :icon="categoryIcon"
                class="q-px-md"
              >
                {{ categoryLabel }}
              </q-badge>
            </div>
            <div class="text-caption">
              {{ category?.label || 'Loading...' }}
            </div>
          </div>
          <div class="col-auto row items-center q-gutter-md">
            <!-- Completion Progress -->
            <q-circular-progress
              :value="completionPercentage"
              size="50px"
              :thickness="0.2"
              :color="statusColor"
              track-color="grey-3"
            >
              <div class="text-white text-bold">{{ completionPercentage }}%</div>
            </q-circular-progress>
            <!-- Status Chip -->
            <q-chip
              :color="statusColor"
              text-color="white"
              :icon="statusIcon"
            >
              {{ documentationStatus }}
            </q-chip>
          </div>
          <q-btn
            flat
            round
            dense
            icon="close"
            @click="close"
          />
        </div>
      </q-card-section>

      <!-- Category Info Banner -->
      <q-banner v-if="category" class="bg-blue-1">
        <template #avatar>
          <q-icon :name="categoryIcon" :color="categoryColor" size="md" />
        </template>
        <div class="text-subtitle2">{{ categoryLabel }} Documentation</div>
        <div class="text-caption text-grey-8">
          Specialized fields for this equipment type with weighted completeness tracking
        </div>
      </q-banner>

      <!-- Tabs -->
      <q-card-section class="q-pa-none">
        <q-tabs
          v-model="activeTab"
          class="text-primary"
          align="left"
          indicator-color="primary"
        >
          <q-tab
            v-if="showFunctionalTab"
            name="functional"
            label="Functional Description"
            icon="description"
          >
            <!-- Complete: tick, In progress: percentage -->
            <q-badge v-if="getTabCompleteness('functional') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('functional') > 0" color="orange" floating>
              {{ getTabCompleteness('functional') }}%
            </q-badge>
          </q-tab>
          <q-tab
            v-if="showProgrammingTab && !showSaxTab"
            name="programming"
            label="Programming"
            icon="code"
          >
            <q-badge v-if="getTabCompleteness('programming') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('programming') > 0" color="orange" floating>
              {{ getTabCompleteness('programming') }}%
            </q-badge>
          </q-tab>
          <q-tab
            v-if="showSaxTab"
            name="sax"
            label="SAX Editor"
            icon="schema"
          >
            <q-badge v-if="getTabCompleteness('sax') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('sax') > 0" color="orange" floating>
              {{ getTabCompleteness('sax') }}%
            </q-badge>
          </q-tab>
          <q-tab
            v-if="showAirflowTab"
            name="airflow"
            label="Airflow Data"
            icon="air"
          >
            <q-badge v-if="getTabCompleteness('airflow') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('airflow') > 0" color="orange" floating>
              {{ getTabCompleteness('airflow') }}%
            </q-badge>
          </q-tab>
          <q-tab
            v-if="showBoardLayoutTab"
            name="board_layout"
            label="Board Layout"
            icon="dashboard"
          >
            <q-badge v-if="getTabCompleteness('board_layout') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('board_layout') > 0" color="orange" floating>
              {{ getTabCompleteness('board_layout') }}%
            </q-badge>
          </q-tab>
          <q-tab
            v-if="showIOViewTab"
            name="io_view"
            label="I/O View"
            icon="cable"
          >
            <q-badge v-if="getTabCompleteness('io_view') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('io_view') > 0" color="orange" floating>
              {{ getTabCompleteness('io_view') }}%
            </q-badge>
          </q-tab>
          <!-- Hide for controllers - config is in I/O View -->
          <q-tab
            v-if="!isController"
            name="category_data"
            :label="category?.label || 'Equipment Data'"
            icon="settings"
          >
            <q-badge v-if="getTabCompleteness('category_data') === 100" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
            <q-badge v-else-if="getTabCompleteness('category_data') > 0" color="orange" floating>
              {{ getTabCompleteness('category_data') }}%
            </q-badge>
          </q-tab>
          <q-tab
            name="devices"
            label="Devices"
            icon="memory"
          >
            <q-badge v-if="deviceCount > 0" :color="documentedDeviceCount === deviceCount ? 'green' : 'primary'" floating>
              {{ documentedDeviceCount }}/{{ deviceCount }}
            </q-badge>
          </q-tab>
          <q-tab
            name="files"
            label="Files"
            icon="attach_file"
          >
            <q-badge v-if="fileCount > 0" color="primary" floating>
              {{ fileCount }}
            </q-badge>
          </q-tab>
          <q-tab
            name="brick"
            label="BRICK Schema"
            icon="schema"
          >
            <q-badge v-if="isSectionComplete('brick')" color="green" floating>
              <q-icon name="check" size="xs" />
            </q-badge>
          </q-tab>
        </q-tabs>

        <q-separator />

        <!-- Tab Panels -->
        <q-tab-panels v-model="activeTab" animated class="q-pa-md">
          <!-- Functional Description -->
          <q-tab-panel v-if="showFunctionalTab" name="functional">
            <div class="row items-center justify-between q-mb-md">
              <div class="text-h6">Functional Description</div>
              <div class="row items-center q-gutter-sm">
                <q-badge
                  v-if="documentation?._inherited?.functional_description"
                  color="orange"
                >
                  <q-icon name="label" size="xs" class="q-mr-xs" />
                  Inherited from: {{ documentation._template_name }}
                </q-badge>
                <q-btn
                  v-else-if="documentation?._template_id && !documentation?._inherited?.functional_description"
                  size="sm"
                  color="primary"
                  outline
                  icon="refresh"
                  label="Reset to Template"
                  @click="resetToTemplate('functional_description')"
                >
                  <q-tooltip>Reset this field to template default</q-tooltip>
                </q-btn>
              </div>
            </div>
            <q-input
              v-model="formData.functional_description"
              type="textarea"
              label="What does this equipment do?"
              rows="10"
              outlined
              hint="Describe the function and purpose of this equipment in the building system"
              @focus="handleFieldFocus('functional_description')"
            />
          </q-tab-panel>

          <!-- Programming -->
          <q-tab-panel v-if="showProgrammingTab && !showSaxTab" name="programming">
            <div class="row items-center justify-between q-mb-md">
              <div class="text-h6">Control Program</div>
              <div class="row items-center q-gutter-sm">
                <q-badge
                  v-if="documentation?._inherited?.programming"
                  color="orange"
                >
                  <q-icon name="label" size="xs" class="q-mr-xs" />
                  Inherited from: {{ documentation._template_name }}
                </q-badge>
                <q-btn
                  v-else-if="documentation?._template_id && !documentation?._inherited?.programming"
                  size="sm"
                  color="primary"
                  outline
                  icon="refresh"
                  label="Reset to Template"
                  @click="resetToTemplate('programming')"
                >
                  <q-tooltip>Reset this field to template default</q-tooltip>
                </q-btn>
              </div>
            </div>
            <q-input
              v-model="formData.programming"
              type="textarea"
              label="Control Programming (XML or pseudocode)"
              rows="20"
              outlined
              hint="Paste XML programming or describe control logic"
              class="code-editor"
              @focus="handleFieldFocus('programming')"
            />
          </q-tab-panel>

          <!-- SAX Editor -->
          <q-tab-panel v-if="showSaxTab" name="sax" class="sax-tab-panel">
            <SaxEditorTab
              :equipment-id="equipment?.id"
              :equipment="equipment"
              :site-id="equipment?.site_id"
              :is-readonly="isController"
              @data-saved="handleSaxDataSaved"
            />
          </q-tab-panel>

          <!-- Board Layout -->
          <q-tab-panel v-if="showBoardLayoutTab" name="board_layout" class="board-layout-tab-panel">
            <DDCBoardCanvas
              v-if="resolvedSiteId"
              :equipment-id="equipment?.id"
              :site-id="resolvedSiteId"
            />
            <div v-else class="text-center q-pa-lg text-grey">
              <q-spinner size="lg" />
              <div class="q-mt-md">Loading board layout...</div>
            </div>
          </q-tab-panel>

          <!-- I/O View (Controller Category) -->
          <q-tab-panel v-if="showIOViewTab" name="io_view" class="io-view-tab-panel">
            <ControllerIOView
              v-if="resolvedSiteId"
              :equipment-id="equipment?.id"
              :site-id="resolvedSiteId"
              :category-data="formData.category_data"
              @update:category-data="formData.category_data = $event"
              @open-sax-editor="handleOpenSaxEditor"
            />
            <div v-else class="text-center q-pa-lg text-grey">
              <q-spinner size="lg" />
              <div class="q-mt-md">Loading I/O view...</div>
            </div>
          </q-tab-panel>

          <!-- Airflow Data -->
          <q-tab-panel v-if="showAirflowTab" name="airflow">
            <div class="row items-center justify-between q-mb-md">
              <div class="text-h6">Airflow Distribution & Configuration</div>
              <q-badge color="blue" class="q-ml-sm">
                <q-icon name="info" size="xs" class="q-mr-xs" />
                Instance-Specific Only
              </q-badge>
            </div>

            <div class="q-mb-md text-caption text-grey-7">
              <q-icon name="info" size="xs" class="q-mr-xs" />
              Note: Airflow data is ALWAYS specific to this equipment instance and never inherited from templates. Flow rate specifications are on the <strong>Equipment Data</strong> tab.
            </div>

            <div class="row q-col-gutter-md">
              <!-- Left Column: Zone & VAV Configuration -->
              <div class="col-12 col-md-4">
                <q-card flat bordered class="full-height">
                  <q-card-section>
                    <div class="text-subtitle1 q-mb-md">Zone Sections Configuration</div>

                    <!-- Zone Sections List -->
                    <q-expansion-item
                      v-for="zone in equipmentZoneSections"
                      :key="zone.id"
                      class="q-mb-sm"
                      header-class="bg-purple-1"
                      expand-separator
                      :default-opened="equipmentZoneSections.length === 1"
                    >
                      <template #header>
                        <q-item-section avatar>
                          <q-icon name="layers" color="purple" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ zone.name || `Zone Section ${zone.id}` }}</q-item-label>
                          <q-item-label caption>
                            Total: {{ zone.flow || 0 }} l/s
                          </q-item-label>
                        </q-item-section>
                      </template>

                      <q-card flat>
                        <q-card-section>
                          <q-input
                            v-model.number="zone.flow"
                            label="Total Flow (l/s)"
                            type="number"
                            outlined
                            dense
                          />

                          <q-separator class="q-my-md" />

                          <!-- Connected Spaces for this Zone -->
                          <div class="text-subtitle2 q-mb-sm">Connected Spaces</div>
                          <q-list v-if="getZoneConnectedSpaces(zone.id).length > 0" dense bordered separator class="rounded-borders q-mb-sm">
                            <q-item v-for="space in getZoneConnectedSpaces(zone.id)" :key="space.id">
                              <q-item-section avatar>
                                <q-icon name="meeting_room" color="orange" size="sm" />
                              </q-item-section>
                              <q-item-section>
                                <q-item-label>{{ space.name }}</q-item-label>
                                <q-item-label caption>{{ space.airflow_cfm || 0 }} l/s</q-item-label>
                              </q-item-section>
                              <q-item-section side>
                                <q-btn
                                  flat
                                  round
                                  dense
                                  icon="delete"
                                  size="sm"
                                  color="negative"
                                  @click.stop="removeZoneSpace(zone.id, space.id)"
                                >
                                  <q-tooltip>Remove Space</q-tooltip>
                                </q-btn>
                              </q-item-section>
                            </q-item>
                          </q-list>

                          <div v-else class="text-grey-6 q-mb-sm">No spaces connected</div>

                          <q-btn
                            color="primary"
                            icon="add"
                            label="Add Space"
                            size="sm"
                            outline
                            class="full-width"
                            @click.stop="showAddZoneSpaceDialog(zone)"
                          />
                        </q-card-section>
                      </q-card>
                    </q-expansion-item>

                    <q-item v-if="equipmentZoneSections.length === 0" class="bg-grey-1 rounded-borders">
                      <q-item-section>
                        <q-item-label class="text-grey-6">No zone sections configured</q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-separator class="q-my-md" />

                    <div class="text-subtitle2 q-mb-sm">Connected Equipment</div>

                    <!-- VAV Connections -->
                    <q-expansion-item
                      v-for="vav in connectedVAVs"
                      :key="vav.id"
                      class="q-mb-sm"
                      header-class="bg-blue-1"
                      expand-separator
                      :default-opened="connectedVAVs.length === 1"
                    >
                      <template #header>
                        <q-item-section avatar>
                          <q-icon name="air" color="primary" />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label>{{ vav.name }}</q-item-label>
                          <q-item-label caption>
                            {{ vav.brick_class || vav.equipment_type }}
                          </q-item-label>
                        </q-item-section>
                        <q-item-section side>
                          <q-btn
                            flat
                            round
                            dense
                            icon="delete"
                            size="sm"
                            color="negative"
                            @click.stop="removeVAV(vav)"
                          >
                            <q-tooltip>Remove Connection</q-tooltip>
                          </q-btn>
                        </q-item-section>
                      </template>

                      <q-card flat>
                        <q-card-section>
                          <q-input
                            v-model.number="vav.airflow_cfm"
                            label="Flow Rate (l/s)"
                            type="number"
                            outlined
                            dense
                          />

                          <q-separator class="q-my-md" />

                          <!-- Connected Spaces for this VAV -->
                          <div class="text-subtitle2 q-mb-sm">Connected Spaces</div>
                          <q-list v-if="getVAVConnectedSpaces(vav.id).length > 0" dense bordered separator class="rounded-borders q-mb-sm">
                            <q-item v-for="space in getVAVConnectedSpaces(vav.id)" :key="space.id">
                              <q-item-section avatar>
                                <q-icon name="meeting_room" color="orange" size="sm" />
                              </q-item-section>
                              <q-item-section>
                                <q-item-label>{{ space.name }}</q-item-label>
                                <q-item-label caption>{{ space.airflow_cfm || 0 }} l/s</q-item-label>
                              </q-item-section>
                              <q-item-section side>
                                <q-btn
                                  flat
                                  round
                                  dense
                                  icon="delete"
                                  size="sm"
                                  color="negative"
                                  @click.stop="removeVAVSpace(vav.id, space.id)"
                                >
                                  <q-tooltip>Remove Space</q-tooltip>
                                </q-btn>
                              </q-item-section>
                            </q-item>
                          </q-list>

                          <div v-else class="text-grey-6 q-mb-sm">No spaces connected</div>

                          <q-btn
                            color="primary"
                            icon="add"
                            label="Add Space"
                            size="sm"
                            outline
                            class="full-width"
                            @click.stop="showAddVAVSpaceDialog(vav)"
                          />
                        </q-card-section>
                      </q-card>
                    </q-expansion-item>

                    <!-- Empty state -->
                    <q-card v-if="connectedVAVs.length === 0" flat bordered>
                      <q-card-section>
                        <div class="text-grey-6">No connections</div>
                      </q-card-section>
                    </q-card>

                    <q-btn
                      color="primary"
                      icon="add"
                      label="Add Connection"
                      class="q-mt-sm full-width"
                      outline
                      @click="openAddVAVDialog"
                    />
                  </q-card-section>
                </q-card>
              </div>

              <!-- Right Column: Sankey Visualization -->
              <div class="col-12 col-md-8">
                <q-card flat bordered class="full-height">
                  <q-card-section class="q-pa-sm">
                    <div class="row items-center justify-between q-mb-sm">
                      <div class="text-subtitle2">Airflow Distribution Diagram</div>
                      <q-btn
                        flat
                        dense
                        icon="refresh"
                        size="sm"
                        :loading="loadingDiagram"
                        @click="renderSankeyDiagram"
                      >
                        <q-tooltip>Refresh Diagram</q-tooltip>
                      </q-btn>
                    </div>

                    <div
                      id="airflow-sankey-container"
                      class="airflow-diagram-container"
                      :style="{ height: '500px', width: '100%' }"
                    >
                      <div v-if="!airflowDataAvailable" class="text-center q-pa-xl text-grey-6">
                        <q-icon name="air" size="64px" color="grey-4" />
                        <div class="q-mt-md">No airflow data configured</div>
                        <div class="text-caption">Add zones, VAVs, or spaces to visualize airflow</div>
                      </div>
                    </div>
                  </q-card-section>
                </q-card>
              </div>
            </div>
          </q-tab-panel>

          <!-- Category-Specific Data (hidden for controllers - config is in I/O View) -->
          <q-tab-panel v-if="!isController" name="category_data">
            <div class="text-h6 q-mb-md">{{ category?.label || 'Equipment' }} Data</div>

            <!-- Weighted Completeness Display (when category is detected) -->
            <div v-if="category" class="completeness-display q-pa-md bg-grey-1 rounded-borders q-mb-md">
              <div class="text-subtitle2 q-mb-sm">Documentation Completeness</div>

              <q-linear-progress
                :value="overallCompleteness"
                :color="completenessColor"
                size="20px"
                class="q-mb-sm"
              >
                <div class="absolute-full flex flex-center">
                  <q-badge :color="completenessColor" :label="`${Math.round(overallCompleteness * 100)}%`" />
                </div>
              </q-linear-progress>

              <div class="row q-gutter-sm text-caption">
                <q-chip size="sm" :color="equipmentCompleteness > 0.7 ? 'green' : 'orange'" text-color="white">
                  Equipment: {{ Math.round(equipmentCompleteness * 100) }}% (70% weight)
                </q-chip>
                <q-chip size="sm" :color="deviceCompleteness > 0.7 ? 'green' : 'orange'" text-color="white">
                  Devices: {{ Math.round(deviceCompleteness * 100) }}% (30% weight)
                </q-chip>
              </div>
            </div>

            <!-- Device Documentation Summary (when category is detected) -->
            <div v-if="category && deviceCount > 0" class="device-summary q-pa-md bg-grey-1 rounded-borders q-mb-md">
              <div class="row items-center justify-between">
                <div>
                  <div class="text-subtitle2">Attached Devices</div>
                  <div class="text-caption text-grey-8">
                    {{ documentedDeviceCount }} of {{ deviceCount }} device{{ deviceCount !== 1 ? 's' : '' }} documented
                  </div>
                </div>
                <q-chip :color="documentedDeviceCount === deviceCount ? 'green' : 'orange'" text-color="white">
                  {{ documentedDeviceCount }}/{{ deviceCount }} documented
                </q-chip>
              </div>
            </div>

            <category-specific-fields
              v-if="category"
              v-model="formData.category_data"
              :category="category.value"
              :fields="category.fields"
            />
          </q-tab-panel>

          <!-- Devices -->
          <q-tab-panel name="devices">
            <div class="text-h6 q-mb-md">Device Documentation</div>
            <device-documentation-section
              v-model="formData.device_data"
              :equipment-id="equipment?.id"
            />
          </q-tab-panel>

          <!-- Files -->
          <q-tab-panel name="files">
            <div class="text-h6 q-mb-md">Attached Files</div>
            <document-file-manager
              :documentable-type="'App\\Models\\SiteEquipment'"
              :documentable-id="equipment?.id"
              file-type="instance_unique"
              @file-uploaded="handleFileUploaded"
              @file-deleted="handleFileDeleted"
            />
          </q-tab-panel>

          <!-- BRICK Schema -->
          <q-tab-panel name="brick">
            <div class="text-h6 q-mb-md">BRICK Schema TTL Definition</div>

            <!-- TTL Preview Card -->
            <q-card flat bordered>
              <q-card-section>
                <div class="row items-center justify-between q-mb-md">
                  <div class="text-subtitle2">Complete Equipment BRICK Definition</div>
                  <q-btn
                    flat
                    dense
                    color="primary"
                    icon="refresh"
                    label="Refresh"
                    :loading="loadingBrickTtl"
                    @click="loadBrickTtl"
                  />
                </div>

                <div v-if="loadingBrickTtl" class="text-center q-pa-xl">
                  <q-spinner color="primary" size="50px" />
                  <div class="text-caption q-mt-md">Generating BRICK TTL...</div>
                </div>

                <div v-else-if="brickTtlError" class="text-center q-pa-xl text-negative">
                  <q-icon name="error" size="48px" />
                  <div class="text-body1 q-mt-md">{{ brickTtlError }}</div>
                </div>

                <div v-else>
                  <pre class="brick-ttl-preview">{{ brickTtl }}</pre>
                </div>
              </q-card-section>
            </q-card>

            <!-- BRICK Info Card -->
            <q-card flat bordered class="q-mt-md">
              <q-card-section>
                <div class="text-subtitle2 q-mb-sm">BRICK Class</div>
                <q-input
                  :model-value="equipment?.brick_class"
                  label="Equipment BRICK Class"
                  outlined
                  dense
                  readonly
                >
                  <template #prepend>
                    <q-icon name="category" />
                  </template>
                </q-input>
              </q-card-section>
            </q-card>
          </q-tab-panel>
        </q-tab-panels>
      </q-card-section>

      <!-- Footer Actions -->
      <q-card-section class="bg-grey-2">
        <div class="row justify-end q-gutter-sm">
          <q-btn
            label="Cancel"
            flat
            @click="close"
          />
          <q-btn
            label="Save"
            color="primary"
            :loading="saving"
            @click="save"
          />
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>

  <!-- Override Warning Dialog -->
  <q-dialog v-model="showOverrideWarning" persistent>
    <q-card style="min-width: 400px">
      <q-card-section>
        <div class="text-h6">Customize Documentation Field?</div>
      </q-card-section>

      <q-card-section>
        <p>
          This field is currently inherited from template:
          <strong>{{ documentation._template_name }}</strong>
        </p>
        <p>
          Editing will create a custom value for this equipment only.
          Changes to the template will no longer affect this field.
        </p>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          v-close-popup
          flat
          label="Keep Template Default"
          color="grey"
          @click="cancelOverride"
        />
        <q-btn
          v-close-popup
          flat
          label="Customize"
          color="primary"
          @click="confirmOverride"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Add VAV Connection Dialog -->
  <q-dialog v-model="showAddVAVDialog">
    <q-card style="min-width: 400px">
      <q-card-section>
        <div class="text-h6">Add Connection</div>
      </q-card-section>

      <q-card-section>
        <q-select
          v-model="selectedVAV"
          :options="availableVAVs"
          option-value="id"
          option-label="name"
          emit-value
          map-options
          label="Select Equipment"
          outlined
          dense
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label caption>{{ scope.opt.brick_class || scope.opt.equipment_type }}</q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="showAddVAVDialog = false" />
        <q-btn
          flat
          label="Add"
          color="primary"
          :disable="!selectedVAV"
          @click="addVAVConnection"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Add Space to Zone Dialog -->
  <q-dialog v-model="showZoneSpaceDialog">
    <q-card style="min-width: 500px">
      <q-card-section>
        <div class="text-h6">Connect {{ selectedZoneForSpace?.name }} to Spaces</div>
      </q-card-section>

      <q-card-section>
        <q-select
          v-model="selectedSpaceForConnection"
          :options="availableSpacesForConnection"
          option-value="id"
          option-label="name"
          label="Select Space"
          outlined
          dense
          emit-value
          map-options
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label caption>
                  {{ formatLevelDisplay(scope.opt) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>

        <q-input
          v-model.number="newSpaceFlow"
          label="Flow Rate (l/s)"
          type="number"
          outlined
          dense
          class="q-mt-md"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="showZoneSpaceDialog = false" />
        <q-btn
          flat
          label="Add"
          color="primary"
          :disable="!selectedSpaceForConnection"
          @click="addZoneSpace"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Add Space to VAV Dialog -->
  <q-dialog v-model="showVAVSpaceDialog">
    <q-card style="min-width: 500px">
      <q-card-section>
        <div class="text-h6">Connect {{ selectedVAVForSpace?.name }} to Spaces</div>
      </q-card-section>

      <q-card-section>
        <q-select
          v-model="selectedSpaceForConnection"
          :options="availableSpacesForConnection"
          option-value="id"
          option-label="name"
          label="Select Space"
          outlined
          dense
          emit-value
          map-options
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label caption>
                  {{ formatLevelDisplay(scope.opt) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>

        <q-input
          v-model.number="newSpaceFlow"
          label="Flow Rate (l/s)"
          type="number"
          outlined
          dense
          class="q-mt-md"
        />
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="showVAVSpaceDialog = false" />
        <q-btn
          flat
          label="Add"
          color="primary"
          :disable="!selectedSpaceForConnection"
          @click="addVAVSpace"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch, onMounted, nextTick } from 'vue';
  import { api } from '@/services/api';
  import { useQuasar } from 'quasar';
  import CategorySpecificFields from './CategorySpecificFields.vue';
  import DeviceDocumentationSection from './DeviceDocumentationSection.vue';
  import DocumentFileManager from './DocumentFileManager.vue';
  import SaxEditorTab from './documentation-tabs/sax/SaxEditorTab.vue';
  import DDCBoardCanvas from './documentation-tabs/DDCBoardCanvas.vue';
  import ControllerIOView from './documentation-tabs/ControllerIOView.vue';
  import { useCategorySystem as useCategorySystemComposable } from '@/composables/brick-schema/documentation/useCategorySystem';

  const $q = useQuasar();

  const props = defineProps({
    equipment: {
      type: Object,
      required: false,
      default: null,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update:modelValue', 'saved', 'open-sax-editor']);

  // State
  const dialogOpen = computed({
    get: () => props.modelValue,
    set: (val) => emit('update:modelValue', val),
  });

  const activeTab = ref('functional');
  const category = ref(null);
  const documentation = ref(null);
  const saving = ref(false);

  const formData = ref({
    functional_description: '',
    programming: '',
    category_data: {},
    device_data: [],
    include_functional: true,
    include_programming: true,
  });

  // Actual devices attached to the equipment (from site_equipment_devices table)
  const equipmentDevices = ref([]);

  // Total device count from actual equipment devices, not just documented ones
  const deviceCount = computed(() => equipmentDevices.value?.length || 0);

  // Count of devices that have a device_part_id (manufacturer/model) assigned
  const documentedDeviceCount = computed(() => {
    if (!equipmentDevices.value?.length) {return 0;}
    // Only count devices that have a device_part_id assigned
    return equipmentDevices.value.filter((device) => {
      // Check if device has device_part_id directly
      if (device.device_part_id || device.device_part) {
        return true;
      }
      // Check if we have documentation with device_part_id for this device
      const savedDoc = formData.value.device_data?.find((d) => d.device_id === device.id);
      return savedDoc && savedDoc.device_part_id;
    }).length;
  });
  const fileCount = ref(0);
  const resolvedSiteId = ref(null);

  // Category System Composable (auto-loads based on BRICK class)
  const {
    categoryLabel,
    categoryIcon,
    categoryColor,
    fieldGroups,
    equipmentCompleteness,
    deviceCompleteness,
    overallCompleteness,
    completenessColor,
    loadCategoryData: loadCategoryDataFromComposable,
    categoryMetadata,
    updateDeviceCompleteness,
  } = useCategorySystemComposable(computed(() => props.equipment));

  // Airflow Management State
  const equipmentZoneSections = ref([]);
  const connectedVAVs = ref([]);
  const showAddVAVDialog = ref(false);
  const selectedVAV = ref(null);
  const availableVAVs = ref([]);

  // Space Management State
  const zoneSpaceConnections = ref({}); // { zoneId: [spaces] }
  const vavSpaceConnections = ref({}); // { vavId: [spaces] }
  const showZoneSpaceDialog = ref(false);
  const showVAVSpaceDialog = ref(false);
  const selectedZoneForSpace = ref(null);
  const selectedVAVForSpace = ref(null);
  const selectedSpaceForConnection = ref(null);
  const availableSpacesForConnection = ref([]);
  const newSpaceFlow = ref(500);

  // BRICK Schema State
  const brickTtl = ref('');
  const loadingBrickTtl = ref(false);
  const brickTtlError = ref(null);

  // Sankey Diagram State
  const loadingDiagram = ref(false);
  const airflowDataAvailable = ref(false);
  const loadingAirflowData = ref(false);

  // Override Warning State
  const showOverrideWarning = ref(false);
  const pendingOverrideField = ref(null);
  const overrideWarningsShown = ref(new Set());

  // Computed Properties
  const showFunctionalTab = computed(() => {
    return formData.value.include_functional !== false;
  });

  const showProgrammingTab = computed(() => {
    return formData.value.include_programming !== false && category.value?.has_programming;
  });

  const showAirflowTab = computed(() => {
    // Show airflow tab for HVAC equipment (AHU, VAV, FCU, etc.)
    const hvacClasses = ['air_handling_unit', 'vav', 'fcu', 'fan_coil_unit', 'terminal_unit', 'air_handler', 'rooftop_unit'];
    const brickClass = props.equipment?.brick_class?.toLowerCase() || '';
    return hvacClasses.some((cls) => brickClass.includes(cls));
  });

  const showBoardLayoutTab = computed(() => {
    // Show board layout tab only for ddc_board category equipment
    return category.value?.value === 'ddc_board';
  });

  const showIOViewTab = computed(() => {
    // Show I/O View tab only for controller category equipment
    return category.value?.value === 'controller';
  });

  const showSaxTab = computed(() => {
    if (!props.equipment?.brick_class) {
      return false;
    }

    const saxEquipmentTypes = [
      'brick:AHU',
      'brick:VAV',
      'brick:FCU',
      'brick:Controller',
      'brick:HVAC_Zone',
      'brick:Air_Handler_Unit',
      'brick:Air_Handling_Unit',
      'brick:Variable_Air_Volume_Box',
      'brick:Fan_Coil_Unit',
    ];

    const brickClass = props.equipment.brick_class.toLowerCase();
    return saxEquipmentTypes.some((type) => brickClass.includes(type.toLowerCase()));
  });

  const isController = computed(() => {
    if (!props.equipment?.brick_class) {return false;}
    return props.equipment.brick_class.toLowerCase().includes('controller');
  });

  const completionPercentage = computed(() => {
    return documentation.value?.completion_percentage || 0;
  });

  const documentationStatus = computed(() => {
    return documentation.value?.documentation_status || 'undocumented';
  });

  const statusColor = computed(() => {
    const status = documentationStatus.value;
    if (status === 'documented') {return 'green';}
    if (status === 'well documented') {return 'light-green';}
    if (status === 'partially documented') {return 'orange';}
    if (status === 'minimal') {return 'deep-orange';}
    return 'red';
  });

  const statusIcon = computed(() => {
    const status = documentationStatus.value;
    if (status === 'documented') {return 'check_circle';}
    if (status === 'well documented') {return 'verified';}
    if (status === 'partially documented') {return 'warning';}
    if (status === 'minimal') {return 'error_outline';}
    return 'cancel';
  });

  // Methods
  const loadCategoryFields = async () => {
    try {
      const data = await api.get('/equipment-documentation/category-fields', {
        brick_class: props.equipment.brick_class,
      });

      // Transform nested fields object into flat array for CategorySpecificFields component
      // API returns: { airflow_data: { label: '...', fields: {...} }, thermal_capacity: {...} }
      const flatFields = [];
      Object.entries(data.fields || {}).forEach(([groupKey, groupData]) => {
        // groupData has { label: '...', fields: {...} } structure
        Object.entries(groupData.fields || {}).forEach(([fieldKey, fieldConfig]) => {
          flatFields.push({
            key: fieldKey,
            group: groupKey,
            groupLabel: groupData.label,
            ...fieldConfig,
          });
        });
      });

      // API service auto-unwraps response, so data is already the category info
      category.value = {
        value: data.category,
        label: data.category_label,
        has_programming: data.has_programming,
        fields: flatFields, // Now a flat array with key properties
      };
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load category fields',
      });
    }
  };

  // Resolve site ID from equipment (may need to fetch from building relationship)
  const resolveSiteId = async () => {
    if (!props.equipment?.id) {return;}

    // Try direct site_id first
    if (props.equipment.site_id) {
      resolvedSiteId.value = props.equipment.site_id;
      return;
    }

    // Try building.site_id
    if (props.equipment.building?.site_id) {
      resolvedSiteId.value = props.equipment.building.site_id;
      return;
    }

    // Fetch equipment details to get site_id
    try {
      const equipmentDetails = await api.get(`/equipment/${props.equipment.id}`);
      if (equipmentDetails.site_id) {
        resolvedSiteId.value = equipmentDetails.site_id;
      } else if (equipmentDetails.building?.site_id) {
        resolvedSiteId.value = equipmentDetails.building.site_id;
      }
    } catch (error) {
      console.error('[CategoryDocumentation] Failed to resolve site ID:', error);
    }
  };

  const loadDocumentation = async () => {
    if (!props.equipment?.id) {
      return;
    }

    try {
      // Resolve site ID for components that need it
      await resolveSiteId();

      // Fetch actual documentation from backend
      const data = await api.get(`/equipment/${props.equipment.id}/documentation`);

      if (data && data.id) {
        // Documentation exists - load it
        documentation.value = {
          id: data.id,
          equipment_id: data.equipment_id,
          use_category_system: data.use_category_system || false,
          documentation_status: data.documentation_status || 'undocumented',
          completion_percentage: data.completion_percentage || 0,
          // Template inheritance fields
          _template_id: data._template_id || null,
          _template_name: data._template_name || null,
          _inherited: data._inherited || {},
          _overridden_categories: data._overridden_categories || [],
          // SAX Editor data - needed for tab completion check
          // Note: API returns programming_sax_data but we normalize to sax_data for frontend
          sax_data: data.programming_sax_data || data.sax_data || null,
          // Board layout data
          board_layout: data.board_layout || null,
          // Controller data for I/O view
          controller_data: data.controller_data || null,
        };

        // Load form data from documentation
        formData.value = {
          functional_description: data.functional_description || '',
          programming: data.programming || '',
          category_data: data.category_data || {},
          device_data: data.device_data || [],
          include_functional: data.include_functional !== false,
          include_programming: data.include_programming !== false,
        };

        // Category system is now automatic - load if equipment has category
        if (props.equipment?.brick_class) {
          await loadCategoryDataFromComposable();
        }
        fileCount.value = data.file_count || 0;
      } else {
        // No documentation exists yet - initialize empty
        documentation.value = {
          equipment_id: props.equipment.id,
          use_category_system: false,
          documentation_status: 'undocumented',
          completion_percentage: 0,
          _template_id: null,
          _template_name: null,
          _inherited: {},
          _overridden_categories: [],
          sax_data: null,
          board_layout: null,
          controller_data: null,
        };

        formData.value = {
          functional_description: '',
          programming: '',
          category_data: {},
          device_data: [],
          include_functional: true,
          include_programming: true,
        };
      }
    } catch (error) {
      // If 404, documentation doesn't exist yet - that's fine
      if (error.response?.status === 404) {
        documentation.value = {
          equipment_id: props.equipment.id,
          use_category_system: false,
          documentation_status: 'undocumented',
          completion_percentage: 0,
          _template_id: null,
          _template_name: null,
          _inherited: {},
          _overridden_categories: [],
          sax_data: null,
          board_layout: null,
          controller_data: null,
        };

        formData.value = {
          functional_description: '',
          programming: '',
          category_data: {},
          device_data: [],
          include_functional: true,
          include_programming: true,
        };
      } else {
        // Actual error
        $q.notify({
          type: 'negative',
          message: 'Failed to load documentation',
        });
      }
    }
  };


  const save = async () => {
    saving.value = true;

    try {
      const payload = {
        equipment_id: props.equipment.id,
        equipment_type: 'site_equipment', // Always use 'site_equipment' for SiteEquipment documentation
        brick_class: props.equipment.brick_class,
        // Category system is always enabled when saving from this dialog
        use_category_system: true,
        include_functional: formData.value.include_functional,
        include_programming: formData.value.include_programming,
        functional_description: formData.value.functional_description,
        programming: formData.value.programming,
        category_data: formData.value.category_data,
        device_data: formData.value.device_data,
      };

      const data = await api.post('/equipment-documentation/category', payload);

      // API auto-unwraps response
      $q.notify({
        type: 'positive',
        message: 'Documentation saved successfully',
      });
      emit('saved', data);
      await loadDocumentation();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.response?.data?.message || 'Failed to save documentation',
      });
    } finally {
      saving.value = false;
    }
  };

  const close = () => {
    dialogOpen.value = false;
  };

  const resetToTemplate = async (category) => {
    $q.dialog({
      title: 'Reset to Template',
      message: `Reset ${category.replace('_', ' ')} back to template default from "${documentation.value._template_name}"? Your custom changes will be lost.`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await api.post(`/equipment/${props.equipment.id}/documentation/reset-to-template/${category}`);

        $q.notify({
          type: 'positive',
          message: `${category.replace('_', ' ')} reset to template default`,
        });

        // Reload documentation to show template values
        await loadDocumentation();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: error.response?.data?.message || 'Failed to reset to template',
        });
      }
    });
  };

  const handleFieldFocus = (fieldName) => {
    // Only show warning if:
    // 1. Field is inherited from template
    // 2. We haven't shown the warning for this field yet this session
    if (documentation.value?._inherited?.[fieldName] &&
      !overrideWarningsShown.value.has(fieldName)) {
      pendingOverrideField.value = fieldName;
      showOverrideWarning.value = true;
    }
  };

  const confirmOverride = () => {
    // Mark that we've shown the warning for this field
    if (pendingOverrideField.value) {
      overrideWarningsShown.value.add(pendingOverrideField.value);
      pendingOverrideField.value = null;
    }
  };

  const cancelOverride = () => {
    // User chose to keep template default - blur the field
    pendingOverrideField.value = null;
    // Optionally reload to reset any changes
    loadDocumentation();
  };

  const handleOpenSaxEditor = (equipment) => {
    // Open SAX editor for the specified equipment
    // This is called from ControllerIOView when user wants to create/edit SAX program
    // The actual navigation is handled by the parent component

    // Close current dialog
    dialogOpen.value = false;

    // Emit event to parent to open SAX editor
    emit('open-sax-editor', equipment);
  };

  /**
   * Handle SAX data saved from the SAX Editor tab
   * Updates documentation.sax_data so the tab tick shows correctly
   */
  const handleSaxDataSaved = (saxData, tabCompleteness = null) => {
    if (documentation.value) {
      documentation.value.sax_data = saxData;
      // Update tab_completeness if returned from API
      if (tabCompleteness) {
        documentation.value.tab_completeness = {
          ...documentation.value.tab_completeness,
          ...tabCompleteness,
        };
      } else {
        // Fallback: set SAX completeness based on whether there are components
        const hasComponents = saxData?.components?.length > 0;
        documentation.value.tab_completeness = {
          ...documentation.value.tab_completeness,
          sax: hasComponents ? 100 : 0,
        };
      }
    }
  };

  /**
   * Get tab completeness percentage.
   * First checks stored tab_completeness, then falls back to live calculation.
   * Returns: 0-100 percentage
   */
  const getTabCompleteness = (section) => {
    // Check stored tab_completeness first
    const stored = documentation.value?.tab_completeness?.[section];
    if (typeof stored === 'number') {
      return stored;
    }

    // Fall back to binary calculation from isSectionComplete
    return isSectionComplete(section) ? 100 : 0;
  };

  const isSectionComplete = (section) => {
    if (section === 'functional') {
      return !!formData.value.functional_description;
    }
    if (section === 'programming') {
      return !!formData.value.programming;
    }
    if (section === 'airflow') {
      // Airflow tab shows zone sections, VAVs, and Sankey diagram
      // Check if there are zone sections OR connected VAVs OR airflow data in category_data
      const hasZoneSections = equipmentZoneSections.value?.length > 0;
      const hasConnectedVAVs = connectedVAVs.value?.length > 0;
      const hasAirflowData = !!(formData.value.category_data?.supply_air_flow_ls ||
        formData.value.category_data?.return_air_flow_ls);
      return hasZoneSections || hasConnectedVAVs || hasAirflowData;
    }
    if (section === 'board_layout') {
      return !!(documentation.value?.board_layout?.items?.length > 0);
    }
    if (section === 'io_view') {
      // Check if any I/O allocations exist
      return !!(documentation.value?.controller_data?.allocations &&
        Object.keys(documentation.value.controller_data.allocations).length > 0);
    }
    if (section === 'category_data') {
      return Object.keys(formData.value.category_data || {}).length > 0;
    }
    if (section === 'brick') {
      return !!brickTtl.value;
    }
    if (section === 'sax') {
      return !!(documentation.value?.sax_data?.components?.length > 0);
    }
    return false;
  };

  const handleFileUploaded = () => {
    fileCount.value++;
  };

  const handleFileDeleted = () => {
    fileCount.value--;
  };

  // Helper function to safely convert to number with fallback
  const toNumber = (value, fallback = 0) => {
    const num = Number(value);
    return isNaN(num) || !isFinite(num) ? fallback : num;
  };

  /**
   * Load actual devices attached to the equipment
   */
  const loadEquipmentDevices = async () => {
    if (!props.equipment?.id) {return;}

    try {
      const response = await api.get(`/site-equipment/${props.equipment.id}/devices`);
      equipmentDevices.value = response || [];
    } catch (error) {
      console.error('[CategoryDocumentation] Failed to load equipment devices:', error);
      equipmentDevices.value = [];
    }
  };

  const loadEquipmentZoneSections = async () => {
    if (!props.equipment?.id) {return;}

    try {
      const equipmentDetails = await api.get(`/equipment/${props.equipment.id}`);

      if (equipmentDetails) {
        const zoneSectionsForEquipment = [];

        // Load zone sections from feeds_equipment
        if (equipmentDetails.feeds_equipment?.length > 0) {
          equipmentDetails.feeds_equipment.forEach((fed) => {
            if (fed.brick_class?.includes('Zone_Section') || fed.brick_class?.includes('Zone')) {
              zoneSectionsForEquipment.push({
                id: fed.id,
                name: fed.name || `Zone Section ${fed.id}`,
                flow: toNumber(fed.properties?.airflow_cfm || fed.flow, 1000),
                brick_class: fed.brick_class,
              });
            }
          });
        }

        equipmentZoneSections.value = zoneSectionsForEquipment;
      }
    } catch (error) {
      equipmentZoneSections.value = [];
    }
  };

  const loadConnectedVAVs = async () => {
    if (!props.equipment?.id) {return;}

    try {
      // Use terminal-units endpoint which loads equipment connected via 'supplies' relationship
      const response = await api.get(`/equipment/${props.equipment.id}/terminal-units`);
      const terminalUnits = response.data || response || {};

      // Backend returns { connected: [...], available: [...] }
      connectedVAVs.value = terminalUnits.connected || [];

      // Load space connections for each VAV using Promise.all to ensure ALL finish before returning
      const spaceLoadPromises = connectedVAVs.value.map(async (vav) => {
        try {
          const spacesResponse = await api.get(`/equipment/${vav.id}/spaces`);
          if (spacesResponse) {
            // API returns { equipment, connected, available } - extract connected array
            const connectedSpaces = spacesResponse.connected || spacesResponse;
            const spacesArray = Array.isArray(connectedSpaces) ? connectedSpaces : [];

            // Create new object to trigger reactivity
            vavSpaceConnections.value = {
              ...vavSpaceConnections.value,
              [vav.id]: spacesArray,
            };

            return { vavId: vav.id, spaces: spacesArray };
          } else {
            vavSpaceConnections.value = {
              ...vavSpaceConnections.value,
              [vav.id]: [],
            };
            return { vavId: vav.id, spaces: [] };
          }
        } catch (error) {
          vavSpaceConnections.value = {
            ...vavSpaceConnections.value,
            [vav.id]: [],
          };
          return { vavId: vav.id, spaces: [] };
        }
      });

      // Wait for ALL space loading operations to complete
      await Promise.all(spaceLoadPromises);

    } catch (error) {
      connectedVAVs.value = [];
    }
  };

  const openAddVAVDialog = async () => {
    // Load available equipment if not loaded
    if (availableVAVs.value.length === 0) {
      try {
        // Get full equipment details with building relationship
        const equipmentDetails = await api.get(`/equipment/${props.equipment.id}`);

        let siteId = null;

        // Try multiple ways to get site_id
        if (equipmentDetails.building?.site_id) {
          siteId = equipmentDetails.building.site_id;
        } else if (equipmentDetails.building_id) {
          const building = await api.get(`/buildings/${equipmentDetails.building_id}`);
          siteId = building.site_id;
        } else if (equipmentDetails.buildingLevel?.building?.site_id) {
          siteId = equipmentDetails.buildingLevel.building.site_id;
        } else if (equipmentDetails.space?.building?.site_id) {
          siteId = equipmentDetails.space.building.site_id;
        }

        if (!siteId) {
          $q.notify({
            type: 'negative',
            message: 'Cannot determine site for this equipment',
          });
          return;
        }

        const hierarchy = await api.get(`/sites/${siteId}/hierarchy`);

        const allEquipment = [];

        // Extract all equipment from hierarchy
        hierarchy.buildings?.forEach((building) => {
          building.levels?.forEach((level) => {
            level.spaces?.forEach((space) => {
              space.equipment?.forEach((eq) => {
                const isCurrent = eq.id === props.equipment.id;
                const isAlreadyConnected = connectedVAVs.value.some((vav) => vav.id === eq.id);

                // Only include VAVs and terminal units (exclude current equipment and already connected)
                if (eq.id !== props.equipment.id &&
                  !isAlreadyConnected &&
                  (eq.brick_class?.includes('Variable_Air_Volume') || eq.brick_class?.includes('Terminal'))) {
                  allEquipment.push(eq);
                }
              });
            });
          });
        });
        availableVAVs.value = allEquipment;
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to load available equipment',
        });
      }
    } else {
    }

    selectedVAV.value = null;
    showAddVAVDialog.value = true;
  };

  const addVAVConnection = async () => {
    if (!selectedVAV.value) {return;}

    try {
      await api.post(`/equipment/${props.equipment.id}/terminal-units`, {
        terminal_unit_id: selectedVAV.value,
        airflow_cfm: 500,
      });

      $q.notify({
        type: 'positive',
        message: 'Connection added successfully',
      });

      showAddVAVDialog.value = false;
      availableVAVs.value = []; // Clear cache to refresh on next open
      await loadConnectedVAVs();
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to add connection',
      });
    }
  };

  const removeVAV = async (vav) => {
    $q.dialog({
      title: 'Remove Connection',
      message: `Remove connection to ${vav.name}?`,
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await api.delete(`/equipment/${props.equipment.id}/terminal-units/${vav.id}`);

        $q.notify({
          type: 'positive',
          message: 'Connection removed successfully',
        });

        availableVAVs.value = []; // Clear cache to refresh on next open
        await loadConnectedVAVs();
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to remove connection',
        });
      }
    });
  };

  // Space Management Functions
  const getZoneConnectedSpaces = (zoneId) => {
    return zoneSpaceConnections.value[zoneId] || [];
  };

  const getVAVConnectedSpaces = (vavId) => {
    return vavSpaceConnections.value[vavId] || [];
  };

  // Helper function to format level display without duplication
  const formatLevelDisplay = (space) => {
    // Get level info from various possible property names
    const levelNumber = space.buildingLevel?.level_number || space.level?.level_number || space.building_level?.level_number;
    const levelName = space.buildingLevel?.name || space.level?.name || space.building_level?.name;

    // If we have a level name, just use it (it often already contains "Level X" or "Ground Floor")
    if (levelName) {
      return levelName;
    }

    // If we only have a number, format it as "Level X"
    if (levelNumber !== undefined && levelNumber !== null) {
      return `Level ${levelNumber}`;
    }

    // Fallback
    return 'Level Unknown';
  };

  const showAddZoneSpaceDialog = async (zone) => {
    selectedZoneForSpace.value = zone;
    selectedSpaceForConnection.value = null;
    newSpaceFlow.value = 500;

    // Load available spaces for this site
    try {
      const site = await getSiteFromEquipment();
      if (site?.id) {
        const spaces = await api.get(`/sites/${site.id}/spaces`);

        // Filter out already connected spaces
        const connectedSpaceIds = (zoneSpaceConnections.value[zone.id] || []).map((s) => s.id);
        availableSpacesForConnection.value = (spaces || []).filter((s) => !connectedSpaceIds.includes(s.id));
      }
    } catch (error) {
      availableSpacesForConnection.value = [];
    }

    showZoneSpaceDialog.value = true;
  };

  const showAddVAVSpaceDialog = async (vav) => {
    selectedVAVForSpace.value = vav;
    selectedSpaceForConnection.value = null;
    newSpaceFlow.value = 500;

    // Load available spaces for this site
    try {
      const site = await getSiteFromEquipment();
      if (site?.id) {
        const spaces = await api.get(`/sites/${site.id}/spaces`);

        // Filter out already connected spaces
        const connectedSpaceIds = (vavSpaceConnections.value[vav.id] || []).map((s) => s.id);
        let filteredSpaces = (spaces || []).filter((s) => !connectedSpaceIds.includes(s.id));

        // Prioritize the VAV's location space at the top of the list
        if (vav.space_id) {
          const vavLocationSpace = filteredSpaces.find((s) => s.id === vav.space_id);
          if (vavLocationSpace) {
            // Remove from current position and add to front
            filteredSpaces = filteredSpaces.filter((s) => s.id !== vav.space_id);
            filteredSpaces.unshift(vavLocationSpace);
          }
        }

        availableSpacesForConnection.value = filteredSpaces;
      }
    } catch (error) {
      availableSpacesForConnection.value = [];
    }

    showVAVSpaceDialog.value = true;
  };

  const addZoneSpace = async () => {
    if (!selectedSpaceForConnection.value || !selectedZoneForSpace.value) {return;}

    const selectedSpace = availableSpacesForConnection.value.find((s) => s.id === selectedSpaceForConnection.value);
    if (!selectedSpace) {return;}

    try {
      // Save to backend API
      const response = await api.post(`/equipment/${selectedZoneForSpace.value.id}/spaces`, {
        space_id: selectedSpaceForConnection.value,
        airflow_cfm: newSpaceFlow.value || 500,
      });


      // Reload spaces from backend to confirm persistence
      try {
        const spacesResponse = await api.get(`/equipment/${selectedZoneForSpace.value.id}/spaces`);
        if (spacesResponse) {
          // API returns { equipment, connected, available } - extract connected array
          const connectedSpaces = spacesResponse.connected || spacesResponse;
          zoneSpaceConnections.value[selectedZoneForSpace.value.id] = Array.isArray(connectedSpaces) ? connectedSpaces : [];
        }
      } catch (reloadError) {
      }

      $q.notify({
        type: 'positive',
        message: 'Space connected to zone and saved to database',
      });

      showZoneSpaceDialog.value = false;

      // Refresh Sankey diagram if visible
      if (activeTab.value === 'airflow') {
        await renderSankeyDiagram();
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.response?.data?.message || 'Failed to connect space',
      });
    }
  };

  const addVAVSpace = async () => {
    if (!selectedSpaceForConnection.value || !selectedVAVForSpace.value) {return;}

    const selectedSpace = availableSpacesForConnection.value.find((s) => s.id === selectedSpaceForConnection.value);
    if (!selectedSpace) {return;}

    try {
      // Save to backend API
      const response = await api.post(`/equipment/${selectedVAVForSpace.value.id}/spaces`, {
        space_id: selectedSpaceForConnection.value,
        airflow_cfm: newSpaceFlow.value || 500,
      });


      // Reload spaces from backend to confirm persistence
      try {
        const spacesResponse = await api.get(`/equipment/${selectedVAVForSpace.value.id}/spaces`);
        if (spacesResponse) {
          // API returns { equipment, connected, available } - extract connected array
          const connectedSpaces = spacesResponse.connected || spacesResponse;
          vavSpaceConnections.value[selectedVAVForSpace.value.id] = Array.isArray(connectedSpaces) ? connectedSpaces : [];
        }
      } catch (reloadError) {
      }

      $q.notify({
        type: 'positive',
        message: 'Space connected to VAV and saved to database',
      });

      showVAVSpaceDialog.value = false;

      // Refresh Sankey diagram if visible
      if (activeTab.value === 'airflow') {
        await renderSankeyDiagram();
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.response?.data?.message || 'Failed to connect space',
      });
    }
  };

  const removeZoneSpace = (zoneId, spaceId) => {
    $q.dialog({
      title: 'Remove Space',
      message: 'Remove this space from the zone?',
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await api.delete(`/equipment/${zoneId}/spaces/${spaceId}`);

        if (zoneSpaceConnections.value[zoneId]) {
          zoneSpaceConnections.value[zoneId] = zoneSpaceConnections.value[zoneId].filter((s) => s.id !== spaceId);
        }

        $q.notify({
          type: 'positive',
          message: 'Space removed from zone',
        });

        // Refresh Sankey diagram if visible
        if (activeTab.value === 'airflow') {
          await renderSankeyDiagram();
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to remove space',
        });
      }
    });
  };

  const removeVAVSpace = (vavId, spaceId) => {
    $q.dialog({
      title: 'Remove Space',
      message: 'Remove this space from the VAV?',
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await api.delete(`/equipment/${vavId}/spaces/${spaceId}`);

        if (vavSpaceConnections.value[vavId]) {
          vavSpaceConnections.value[vavId] = vavSpaceConnections.value[vavId].filter((s) => s.id !== spaceId);
        }

        $q.notify({
          type: 'positive',
          message: 'Space removed from VAV',
        });

        // Refresh Sankey diagram if visible
        if (activeTab.value === 'airflow') {
          await renderSankeyDiagram();
        }
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: 'Failed to remove space',
        });
      }
    });
  };

  const getSiteFromEquipment = async () => {
    // Get site from equipment
    if (props.equipment.building?.site_id) {
      return { id: props.equipment.building.site_id };
    }

    // Try to fetch equipment details
    try {
      const equipmentDetails = await api.get(`/equipment/${props.equipment.id}`);
      if (equipmentDetails.building?.site_id) {
        return { id: equipmentDetails.building.site_id };
      }
    } catch (error) {
      console.error('Failed to fetch equipment details for site lookup:', error);
    }

    return null;
  };

  const loadBrickTtl = async () => {
    if (!props.equipment?.id) {
      brickTtlError.value = 'Equipment ID not available';
      return;
    }

    loadingBrickTtl.value = true;
    brickTtlError.value = null;

    try {
      const response = await api.get(`/equipment/${props.equipment.id}/brick-ttl`);
      brickTtl.value = response.ttl || response.data?.ttl || response || '# No TTL data available';
    } catch (error) {
      brickTtlError.value = error.response?.data?.message || 'Failed to load BRICK TTL definition';
    } finally {
      loadingBrickTtl.value = false;
    }
  };

  const renderSankeyDiagram = async () => {

    // Show diagram if we have any data to display
    const hasZones = equipmentZoneSections.value.length > 0;
    const hasVAVs = connectedVAVs.value.length > 0;
    const hasSpaceConnections = Object.keys(vavSpaceConnections.value).length > 0 || Object.keys(zoneSpaceConnections.value).length > 0;
    const hasSupplyFlow = formData.value.airflow_data?.supply_flow > 0;


    airflowDataAvailable.value = hasZones || hasVAVs || hasSpaceConnections || hasSupplyFlow;

    if (!airflowDataAvailable.value) {
      return;
    }

    loadingDiagram.value = true;

    try {
      // Wait for next tick to ensure DOM is ready
      await nextTick();

      // Dynamically import D3 and d3-sankey
      const d3 = await import('d3');
      const { sankey, sankeyLinkHorizontal, sankeyLeft } = await import('d3-sankey');

      // Clear previous diagram
      const container = document.getElementById('airflow-sankey-container');
      if (!container) {
        return;
      }

      // Check if container is visible
      const isVisible = container.offsetParent !== null;
      if (!isVisible) {
        return;
      }

      // Ensure container has dimensions
      if (container.offsetWidth === 0 || container.offsetHeight === 0) {
        setTimeout(() => renderSankeyDiagram(), 100);
        return;
      }

      d3.select(container).selectAll('*').remove();

      // Build nodes and links
      const nodes = [];
      const links = [];
      const nodeMap = new Map();

      // AHU node (main equipment)
      const ahuFlow = toNumber(formData.value.airflow_data?.supply_flow, 1000);
      const ahuNode = {
        id: 'ahu',
        name: props.equipment?.name || 'AHU',
        type: 'ahu',
        column: 0,
      };
      nodes.push(ahuNode);
      nodeMap.set('ahu', ahuNode);


      // Sort zones alphabetically
      const sortedZones = [...equipmentZoneSections.value].sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' });
      });

      // Add zone nodes
      sortedZones.forEach((zone) => {
        const zoneId = `zone-${zone.id}`;
        const zoneFlow = toNumber(zone.flow, 500);


        const zoneNode = {
          id: zoneId,
          name: zone.name || `Zone ${zone.id}`,
          type: 'zone',
          column: 1,
        };
        nodes.push(zoneNode);
        nodeMap.set(zoneId, zoneNode);

        // Link AHU to zone
        links.push({
          source: 'ahu',
          target: zoneId,
          value: zoneFlow,
        });

        // Add space connections for this zone
        const zoneSpaces = zoneSpaceConnections.value[zone.id] || [];
        zoneSpaces.forEach((space) => {
          const spaceId = `space-${space.id}`;

          if (!nodeMap.has(spaceId)) {
            const spaceNode = {
              id: spaceId,
              name: space.name,
              type: 'space',
              column: 2,
            };
            nodes.push(spaceNode);
            nodeMap.set(spaceId, spaceNode);
          }

          const spaceFlow = toNumber(space.airflow_cfm, 500);

          links.push({
            source: zoneId,
            target: spaceId,
            value: spaceFlow,
          });
        });
      });

      // Add VAV nodes
      connectedVAVs.value.forEach((vav, index) => {
        const vavId = `vav-${vav.id}`;
        const vavFlow = toNumber(vav.airflow_cfm, 500);


        const vavNode = {
          id: vavId,
          name: vav.name || `VAV ${index + 1}`,
          type: 'vav',
          column: 1,
        };
        nodes.push(vavNode);
        nodeMap.set(vavId, vavNode);

        // Link AHU to VAV
        links.push({
          source: 'ahu',
          target: vavId,
          value: vavFlow,
        });

        // Add space connections for this VAV
        const vavSpaces = vavSpaceConnections.value[vav.id] || [];
        vavSpaces.forEach((space) => {
          const spaceId = `space-${space.id}`;

          if (!nodeMap.has(spaceId)) {
            const spaceNode = {
              id: spaceId,
              name: space.name,
              type: 'space',
              column: 2,
            };
            nodes.push(spaceNode);
            nodeMap.set(spaceId, spaceNode);
          }

          const spaceFlow = toNumber(space.airflow_cfm, 500);

          links.push({
            source: vavId,
            target: spaceId,
            value: spaceFlow,
          });
        });
      });

      // Create SVG
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const margin = { top: 20, right: 20, bottom: 20, left: 20 };
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;


      // Validate dimensions are valid numbers
      if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) {
        loadingDiagram.value = false;
        return;
      }

      // Validate all link values are valid numbers
      const invalidLinks = links.filter((link) => !isFinite(link.value) || link.value <= 0);
      if (invalidLinks.length > 0) {
        // Fix invalid links by setting to default value
        invalidLinks.forEach((link) => {
          link.value = 500;
        });
      }


      const svg = d3.select(container)
        .append('svg')
        .attr('width', containerWidth)
        .attr('height', containerHeight)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Create Sankey generator
      const sankeyGenerator = sankey()
        .nodeId((d) => d.id)
        .nodeWidth(20)
        .nodePadding(10)
        .extent([[0, 0], [width, height]])
        .nodeAlign(sankeyLeft);

      const graphData = {
        nodes: nodes.map((d) => ({ ...d })),
        links: links.map((d) => ({ ...d })),
      };


      // Generate layout
      const graph = sankeyGenerator(graphData);


      // Validate node positions after layout
      const invalidNodes = graph.nodes.filter((node) =>
        !isFinite(node.x0) || !isFinite(node.x1) ||
        !isFinite(node.y0) || !isFinite(node.y1),
      );

      if (invalidNodes.length > 0) {
        loadingDiagram.value = false;
        $q.notify({
          type: 'warning',
          message: 'Unable to render diagram - invalid data detected',
        });
        return;
      }

      // Create gradient definitions for links
      const defs = svg.append('defs');

      graph.links.forEach((link, i) => {
        const gradient = defs.append('linearGradient')
          .attr('id', `gradient-${i}`)
          .attr('gradientUnits', 'userSpaceOnUse')
          .attr('x1', link.source.x1)
          .attr('x2', link.target.x0);

        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', getNodeColor(link.source.type));

        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', getNodeColor(link.target.type));
      });

      // Draw links
      svg.append('g')
        .selectAll('path')
        .data(graph.links)
        .enter()
        .append('path')
        .attr('d', sankeyLinkHorizontal())
        .attr('stroke', (d, i) => `url(#gradient-${i})`)
        .attr('stroke-width', (d) => Math.max(1, d.width))
        .attr('fill', 'none')
        .attr('opacity', 0.4)
        .on('mouseover', function(event, d) {
          d3.select(this).attr('opacity', 0.7);
        })
        .on('mouseout', function(event, d) {
          d3.select(this).attr('opacity', 0.4);
        });

      // Draw nodes
      svg.append('g')
        .selectAll('rect')
        .data(graph.nodes)
        .enter()
        .append('rect')
        .attr('x', (d) => d.x0)
        .attr('y', (d) => d.y0)
        .attr('height', (d) => Math.max(2, d.y1 - d.y0))
        .attr('width', (d) => d.x1 - d.x0)
        .attr('fill', (d) => getNodeColor(d.type))
        .attr('opacity', 0.8)
        .on('mouseover', function(event, d) {
          d3.select(this).attr('opacity', 1);
        })
        .on('mouseout', function(event, d) {
          d3.select(this).attr('opacity', 0.8);
        });

      // Draw node labels
      svg.append('g')
        .selectAll('text')
        .data(graph.nodes)
        .enter()
        .append('text')
        .attr('x', (d) => d.x0 < width / 2 ? d.x1 + 6 : d.x0 - 6)
        .attr('y', (d) => (d.y1 + d.y0) / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', (d) => d.x0 < width / 2 ? 'start' : 'end')
        .attr('font-size', '12px')
        .text((d) => d.name);


    } catch (error) {
      console.error('Failed to render Sankey diagram:', error);
    } finally {
      loadingDiagram.value = false;
    }
  };

  const getNodeColor = (type) => {
    switch (type) {
      case 'ahu': return '#1976d2';
      case 'zone': return '#9c27b0';
      case 'vav': return '#0288d1';
      case 'space': return '#f57c00';
      default: return '#757575';
    }
  };

  // Lifecycle
  onMounted(async () => {
    if (props.equipment?.brick_class) {
      // Load both local category data and composable metadata
      await Promise.all([
        loadCategoryFields(),
        loadCategoryDataFromComposable(),
      ]);
    }
    if (props.equipment?.id) {
      await loadDocumentation();
      // Load actual equipment devices for accurate count
      await loadEquipmentDevices();

      // Pre-load airflow data for HVAC equipment (so tick shows correctly)
      if (showAirflowTab.value) {
        // Load zone sections and VAVs without rendering diagram yet
        await loadEquipmentZoneSections();
        await loadConnectedVAVs();
      }
    }
  });

  watch(() => props.equipment, async (newEquipment, oldEquipment) => {
    // Only reset data if equipment ID changed (not on reopen of same equipment)
    if (newEquipment?.id !== oldEquipment?.id) {
      equipmentZoneSections.value = [];
      connectedVAVs.value = [];
      equipmentDevices.value = [];
    }

    if (newEquipment?.brick_class) {
      // Load both local category data and composable metadata
      await Promise.all([
        loadCategoryFields(),
        loadCategoryDataFromComposable(),
      ]);
    }
    if (newEquipment?.id) {
      await loadDocumentation();
      // Load actual equipment devices for accurate count
      await loadEquipmentDevices();

      // Pre-load airflow data for HVAC equipment (so tick shows correctly)
      if (showAirflowTab.value) {
        await loadEquipmentZoneSections();
        await loadConnectedVAVs();
      }
    }
  });

  // Helper to load airflow data once (prevents duplicate calls)
  const loadAirflowData = async () => {
    if (loadingAirflowData.value) {
      return;
    }

    loadingAirflowData.value = true;
    try {
      await loadEquipmentZoneSections();
      await loadConnectedVAVs(); // Wait for VAVs AND their spaces to finish loading
      // Render Sankey diagram after ALL data is loaded
      await nextTick();
      await renderSankeyDiagram();
    } finally {
      loadingAirflowData.value = false;
    }
  };

  // Watch for dialog opening to load initial data for current tab
  watch(dialogOpen, async (isOpen) => {
    if (isOpen && props.equipment?.id) {

      // If opening directly on airflow tab, load airflow data
      if (activeTab.value === 'airflow') {
        await loadAirflowData();
      }

      // If opening on brick tab, load TTL
      if (activeTab.value === 'brick' && !brickTtl.value) {
        await loadBrickTtl();
      }
    }
  });

  // Watch for tab activation to load data
  watch(activeTab, async (newTab) => {
    if (newTab === 'brick' && !brickTtl.value) {
      await loadBrickTtl();
    }
    if (newTab === 'airflow' && dialogOpen.value) {
      await loadAirflowData();
    }
  });

  // Watch device data changes to update completeness calculation
  watch(
    () => formData.value.device_data,
    (newDeviceData) => {
      updateDeviceCompleteness(newDeviceData);
    },
    { deep: true, immediate: true },
  );
</script>

<style scoped>
.category-documentation-dialog {
  height: 100%;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Main card needs to be flex container */
.category-documentation-dialog :deep(.q-card__section) {
  flex-shrink: 0;
}

/* The tab panels section should grow to fill available space */
.category-documentation-dialog > :deep(.q-card__section:nth-child(3)) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Ensure tab panels fill available space */
.category-documentation-dialog :deep(.q-tab-panels) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.category-documentation-dialog :deep(.q-tab-panel) {
  height: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

/* SAX Tab Panel - no padding, full height */
.sax-tab-panel {
  padding: 0 !important;
}

/* Board Layout Tab Panel - no padding, full height */
.board-layout-tab-panel {
  padding: 0 !important;
}

/* I/O View Tab Panel - no padding, full height */
.io-view-tab-panel {
  padding: 0 !important;
}

.code-editor :deep(textarea) {
  font-family: 'Courier New', monospace;
  font-size: 13px;
}

.brick-ttl-preview {
  font-family: 'Courier New', 'Monaco', monospace;
  font-size: 12px;
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 600px;
  overflow-y: auto;
  line-height: 1.5;
  color: #2c3e50;
}

.airflow-diagram-container {
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.airflow-diagram-container :deep(svg) {
  display: block;
}
</style>
